import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import API from '../services/api';
import './vendorView.css';
import { formatContractorId } from '../utils/idFormat';
import AdminUserHistoryPanel from './AdminUserHistoryPanel';

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-IN') : '—');
const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const orDash = (v) => (v === 0 ? '0' : (v == null || String(v).trim() === '' ? '—' : String(v)));
const bagSummary = (rows, unit) => {
  const summary = Array.isArray(rows) ? rows.map((row) => `${row.bagSize}`).join(', ') : '';
  return summary || String(unit || '').trim() || '—';
};

const toFileURL = (raw) => {
  if (!raw) return null;
  const v = String(raw).trim();
  if (!v) return null;
  if (v.startsWith('data:')) return v;
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith('/')) return v;
  return `/uploads/${v}`;
};

const KV = ({ label, value, mono = false }) => (
  <div className="vx-kv">
    <div className="vx-kv-label">{label}</div>
    <div className={`vx-kv-value ${mono ? 'mono' : ''}`}>{orDash(value)}</div>
  </div>
);

const toFormState = (data) => {
  const profile = data?.profile || {};
  return {
    fullName: data?.fullName || '',
    email: data?.email || '',
    mobile: data?.mobile || '',
    approved: !!data?.approved,
    contractorName: profile.contractorName || data?.fullName || '',
    logo: profile.logo || '',
    millName: profile.millName || '',
    address: profile.address || '',
    contactPerson: profile.contactPerson || '',
    contactMobile: profile.contactMobile || data?.mobile || '',
    notes: profile.notes || '',
    status: profile.status === 'inactive' ? 'inactive' : 'active',
  };
};

const AdminContractorView = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [detail, setDetail] = useState(null);
  const [entries, setEntries] = useState([]);
  const [editing, setEditing] = useState(false);
  const [imgFail, setImgFail] = useState(false);
  const [form, setForm] = useState(() => toFormState(null));

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [dRes, eRes] = await Promise.all([
          API.get(`/admin/contractors/${id}`),
          API.get('/admin/contractor-work-entries', { params: { contractorId: id } }),
        ]);
        if (ignore) return;
        const nextDetail = dRes.data || null;
        setDetail(nextDetail);
        setForm(toFormState(nextDetail));
        setEntries(Array.isArray(eRes.data?.entries) ? eRes.data.entries : []);
        setImgFail(false);
      } catch (e) {
        if (!ignore) {
          setError(e?.response?.data?.message || 'Failed to load contractor details');
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, [id]);

  useEffect(() => {
    if (location.hash !== '#history') return;
    const node = document.getElementById('contractor-history');
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash, loading]);

  const totalAmount = useMemo(
    () => entries.reduce((sum, e) => sum + Number(e.totalAmount || 0), 0),
    [entries]
  );

  const totalQty = useMemo(
    () => entries.reduce((sum, e) => sum + Number(e.quantity || 0), 0),
    [entries]
  );

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    if (name === 'mobile' || name === 'contactMobile') {
      setForm((prev) => ({ ...prev, [name]: String(nextValue).replace(/\D/g, '').slice(0, 15) }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: nextValue }));
  };

  const startEdit = () => {
    setMessage('');
    setError('');
    setEditing(true);
    setForm(toFormState(detail));
  };

  const cancelEdit = () => {
    setEditing(false);
    setMessage('');
    setError('');
    setForm(toFormState(detail));
  };

  const saveChanges = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setMessage('');
      const payload = {
        fullName: form.fullName,
        email: form.email,
        mobile: form.mobile,
        approved: !!form.approved,
        contractorName: form.contractorName,
        logo: form.logo,
        millName: form.millName,
        address: form.address,
        contactPerson: form.contactPerson,
        contactMobile: form.contactMobile,
        notes: form.notes,
        status: form.status,
      };
      const res = await API.put(`/admin/contractors/${id}`, payload);
      const nextDetail = res.data?.contractor || null;
      setDetail(nextDetail);
      setForm(toFormState(nextDetail));
      setEditing(false);
      setImgFail(false);
      setMessage(res.data?.message || 'Contractor updated successfully');
    } catch (e1) {
      setError(e1?.response?.data?.message || 'Failed to update contractor');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" /></div>;
  }

  const p = detail?.profile || {};
  const logoUrl = toFileURL(p.logo);
  const contractorDisplayId = formatContractorId(detail || {}, detail || {});
  const initials = (p.contractorName || detail?.fullName || 'C')
    .split(' ')
    .map((s) => s.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="vx vx-light">
      <Container className="py-3 vx-wrap">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h4 className="mb-0">Contractor Details</h4>
          <div className="d-flex gap-2">
            {!editing ? <Button onClick={startEdit}>Edit Details</Button> : null}
            <Button variant="outline-secondary" onClick={() => navigate('/admin/contractors')}>Back</Button>
          </div>
        </div>

        {message && <Alert variant="success" className="mb-3">{message}</Alert>}
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

        <div className="card-gradient mb-3">
          <div className="vx-hero">
            <div className="vx-hero-left">
              {logoUrl && !imgFail ? (
                <img
                  src={logoUrl}
                  alt="Contractor logo"
                  className="vx-avatar vx-avatar-img"
                  onError={() => setImgFail(true)}
                  loading="lazy"
                />
              ) : (
                <div className="vx-avatar">{initials}</div>
              )}
              <div>
                <div className="vx-title clamp-1">{orDash(p.contractorName || detail?.fullName)}</div>
                <div className="vx-subtitle clamp-1">{orDash(p.millName || 'Mill name not added')}</div>
                <div className="vx-chips">
                  <span className="vx-chip vx-chip-green">
                    <span style={{ fontSize: 12 }}>●</span> {detail?.approved ? 'Approved' : 'Pending'}
                  </span>
                  <span className="vx-chip vx-chip-blue">{p.status === 'inactive' ? 'Inactive' : 'Active'}</span>
                  <span className="vx-chip vx-chip-purple">Entries: {entries.length}</span>
                </div>
              </div>
            </div>
            <div className="vx-hero-right">
              <div className="vx-meta mono">
                <div>ID: {contractorDisplayId || '—'}</div>
                <div>Created: {fmtDate(detail?.createdAt)}</div>
                <div>Updated: {fmtDate(detail?.updatedAt)}</div>
              </div>
            </div>
          </div>
        </div>

        {!editing ? (
          <>
            <Card className="card-glass mb-3">
              <Card.Body>
                <Row className="g-2">
                  <Col md={6}>
                    <KV label="Account Name" value={detail?.fullName} />
                    <KV label="Login Mobile" value={detail?.mobile} mono />
                    <KV label="Email" value={detail?.email} />
                  </Col>
                  <Col md={6}>
                    <KV label="Contractor Name" value={p.contractorName || detail?.fullName} />
                    <KV label="Mill Name" value={p.millName} />
                    <KV label="Contact Person" value={p.contactPerson} />
                    <KV label="Contact Mobile" value={p.contactMobile} mono />
                  </Col>
                  <Col md={12}><div className="vx-divider" /></Col>
                  <Col md={8}>
                    <KV label="Address" value={p.address} />
                    <KV label="Notes" value={p.notes} />
                  </Col>
                  <Col md={4}>
                    <KV label="Logo" value={p.logo ? 'Configured' : 'Not set'} />
                    {logoUrl && !imgFail ? (
                      <div className="mt-2">
                        <img
                          src={logoUrl}
                          alt="Contractor logo preview"
                          style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 14, objectFit: 'cover' }}
                          onError={() => setImgFail(true)}
                        />
                      </div>
                    ) : (
                      <div className="text-muted small mt-2">Logo not available.</div>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Row className="g-3 mb-3">
              <Col md={4}>
                <Card className="card-glass h-100">
                  <Card.Body>
                    <div className="vx-kv-label">Total Entries</div>
                    <div className="vx-title" style={{ fontSize: 28 }}>{entries.length}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="card-glass h-100">
                  <Card.Body>
                    <div className="vx-kv-label">Total Quantity</div>
                    <div className="vx-title" style={{ fontSize: 28 }}>{Number(totalQty || 0).toLocaleString('en-IN')}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="card-glass h-100">
                  <Card.Body>
                    <div className="vx-kv-label">Total Amount</div>
                    <div className="vx-title" style={{ fontSize: 28 }}>{fmtMoney(totalAmount)}</div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        ) : (
          <Card className="card-glass mb-3">
            <Card.Body>
              <Form onSubmit={saveChanges}>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Account Name</Form.Label>
                      <Form.Control name="fullName" value={form.fullName} onChange={onChange} required />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Login Mobile</Form.Label>
                      <Form.Control name="mobile" value={form.mobile} onChange={onChange} required placeholder="10-15 digits" />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control name="email" value={form.email} onChange={onChange} placeholder="name@example.com" />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Contractor Name</Form.Label>
                      <Form.Control name="contractorName" value={form.contractorName} onChange={onChange} required />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Mill Name</Form.Label>
                      <Form.Control name="millName" value={form.millName} onChange={onChange} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Logo URL</Form.Label>
                      <Form.Control name="logo" value={form.logo} onChange={onChange} placeholder="https://..." />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Contact Person</Form.Label>
                      <Form.Control name="contactPerson" value={form.contactPerson} onChange={onChange} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Contact Mobile</Form.Label>
                      <Form.Control name="contactMobile" value={form.contactMobile} onChange={onChange} required placeholder="10-15 digits" />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Status</Form.Label>
                      <Form.Select name="status" value={form.status} onChange={onChange}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Address</Form.Label>
                      <Form.Control as="textarea" rows={2} name="address" value={form.address} onChange={onChange} />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Notes</Form.Label>
                      <Form.Control as="textarea" rows={4} name="notes" value={form.notes} onChange={onChange} />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Check
                      type="switch"
                      id="approved"
                      name="approved"
                      checked={!!form.approved}
                      onChange={onChange}
                      label="Approved"
                    />
                  </Col>
                </Row>
                <div className="d-flex gap-2 mt-4">
                  <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
                  <Button type="button" variant="outline-secondary" onClick={cancelEdit} disabled={saving}>Cancel</Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        )}

        <Card className="card-glass mb-3" id="contractor-history">
          <Card.Body>
            <h5 className="mb-3">History</h5>
            <AdminUserHistoryPanel role="contractor" userId={id} />
          </Card.Body>
        </Card>

        <Card className="card-glass">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Work Entries</h5>
              <div className="text-muted">Total: {fmtMoney(totalAmount)}</div>
            </div>

            {entries.length === 0 ? (
              <div className="text-muted">No entries found for this contractor.</div>
            ) : (
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Worker</th>
                    <th>Work Type</th>
                    <th>Qty</th>
                    <th>Bag Details</th>
                    <th>Rate</th>
                    <th>Amount</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e._id}>
                      <td>{fmtDate(e.workDate)}</td>
                      <td>{e.workerName || '—'}</td>
                      <td>{e.workType || '—'}</td>
                      <td>{e.quantity || 0} {e.unit || ''}</td>
                      <td>{bagSummary(e.bagDetails, e.unit)}</td>
                      <td>{fmtMoney(e.unitRate)}</td>
                      <td>{fmtMoney(e.totalAmount)}</td>
                      <td>{e.remarks || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default AdminContractorView;
